import React from "react";
import { 
  Server, ShoppingCart, Code, Palette, Globe, Lock, Shield, 
  Bug, KeyRound, Headphones, MonitorSmartphone, Share2, 
  Camera, Wrench 
} from "lucide-react";
import { motion } from "framer-motion";
import { useCart } from "../shared/CartContext"; // <-- add this import
// import { Link } from "react-router-dom";

export default function OtherServices() {
  const { addItem } = useCart(); // <-- use the cart context

  return (
    <div>
      <section className="relative bg-[linear-gradient(160deg,#1e40af_0%,#0891b2_100%)] text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold">Additional Services</h1>
          <p className="mt-4 text-blue-100 max-w-2xl mx-auto text-lg">
            Comprehensive solutions to help your business grow online.
          </p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12">
        {services(addItem)}
      </div>
    </div>
  );
}

// Pass addItem as argument
function services(addItem) {
  const services = [
  {
    icon: <Server className="w-8 h-8 text-blue-600" />,
    title: "Web Hosting",
    desc: "Reliable and high-speed web hosting for personal and business websites.",
    highlights: ["99.9% uptime", "Fast SSD storage", "24/7 monitoring"],
  },
  {
    icon: <Camera className="w-8 h-8 text-yellow-600" />,
    title: "CCTV Installation",
    desc: "Professional installation of CCTV systems for homes and offices.",
    highlights: ["HD cameras", "Remote access", "Maintenance services"],
  },
  {
    icon: <Wrench className="w-8 h-8 text-gray-600" />,
    title: "Custom Software Solutions",
    desc: "Tailor-made software to streamline and automate your business.",
    highlights: ["Custom CRM/ERP", "API integration", "Automation tools"],
  },
  {
    icon: <Server className="w-8 h-8 text-indigo-600" />,
    title: "VPS (Virtual Private Server)",
    desc: "Powerful and scalable VPS hosting tailored for developers and businesses.",
    highlights: ["Full root access", "SSD-powered", "Flexible scaling"],
  },
  {
    icon: <ShoppingCart className="w-8 h-8 text-orange-600" />,
    title: "E-Commerce Development",
    desc: "Custom online stores with secure checkout and modern designs.",
    highlights: ["Shopify/WooCommerce", "Payment gateways", "Inventory management"],
  },
  {
    icon: <Code className="w-8 h-8 text-indigo-600" />,
    title: "Website Development",
    desc: "Modern, responsive websites built with the latest technologies.",
    highlights: ["React/Next.js", "SEO-friendly", "Fast loading"],
  },
  {
    icon: <Palette className="w-8 h-8 text-pink-600" />,
    title: "Web Design",
    desc: "Elegant and user-friendly web designs that engage and convert visitors.",
    highlights: ["UI/UX focused", "Wireframing", "Responsive layouts"],
  },
  {
    icon: <Globe className="w-8 h-8 text-green-600" />,
    title: "CMS Setup",
    desc: "Simplify content management with easy-to-use CMS platforms.",
    highlights: ["WordPress", "Custom themes", "Plugin integration"],
  },
  {
    icon: <Lock className="w-8 h-8 text-red-600" />,
    title: "SSL Certificate Setup",
    desc: "Secure your website with SSL certificates for data protection and trust.",
    highlights: ["HTTPS setup", "Certificate renewal", "Security audits"],
  },
  {
    icon: <Shield className="w-8 h-8 text-blue-700" />,
    title: "Firewall Configuration",
    desc: "Protect your servers and websites with advanced firewall setups.",
    highlights: ["Network protection", "Intrusion prevention", "Custom rules"],
  },
  {
    icon: <Bug className="w-8 h-8 text-yellow-600" />,
    title: "Malware Removal",
    desc: "Clean and restore infected websites and servers with expert tools.",
    highlights: ["Deep scans", "Threat removal", "Hardening & patching"],
  },
  {
    icon: <KeyRound className="w-8 h-8 text-teal-600" />,
    title: "Data Encryption",
    desc: "Keep your data safe with advanced encryption and secure storage.",
    highlights: ["End-to-end encryption", "Secure backups", "Compliance-ready"],
  },
  {
    icon: <Headphones className="w-8 h-8 text-purple-600" />,
    title: "Technical Support",
    desc: "Round-the-clock support to keep your systems running smoothly.",
    highlights: ["24/7 assistance", "Issue resolution", "Remote troubleshooting"],
  },
  {
    icon: <MonitorSmartphone className="w-8 h-8 text-cyan-600" />,
    title: "Remote IT Support",
    desc: "Instant remote IT help to fix software and system issues efficiently.",
    highlights: ["Remote desktop", "System diagnostics", "Quick response"],
  },
  {
    icon: <Share2 className="w-8 h-8 text-blue-500" />,
    title: "Social Media Marketing",
    desc: "Boost your online presence with targeted social media strategies.",
    highlights: ["Campaign management", "Ad optimization", "Analytics reports"],
  },
];

  // Add a fixed price for each service (customize as needed)
  const servicePrices = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
  ];

  return (
    <div className="text-gray-800">
      {/* Services Grid */}
      <section className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              viewport={{ once: true }}
              className="flex flex-col bg-white border border-gray-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-transform hover:-translate-y-1"
            >
              <div>
                <div className="flex items-center gap-3 mb-3">
                  {s.icon}
                  <h3 className="text-xl font-semibold text-gray-900">
                    {s.title}
                  </h3>
                </div>
                <p className="text-gray-600">{s.desc}</p>
                <ul className="mt-4 text-sm text-gray-700 space-y-1">
                  {s.highlights.map((h, j) => (
                    <li key={j}>• {h}</li>
                  ))}
                </ul>
              </div>

              {/* Add to Cart Button pinned at bottom */}
              <div className="mt-auto pt-6">
                <button
                  className="w-full inline-block text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 rounded-lg font-medium hover:opacity-90 transition"
                  onClick={() =>
                    addItem(
                      `service:${s.title}`,
                      {
                        title: s.title,
                        subtitle: s.desc,
                        price: servicePrices[i],
                      }
                    )
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4" /> Add to cart
                  </span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
